import { useCallback, useState } from "react";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";
import { useRpcQuery } from "../../../runtime/hooks/useRpc";

/** Why an engine cannot be used. Mirrors `services::agents::readiness`. */
export type ReadinessReason =
  | "ok"
  | "binary_missing"
  | "not_signed_in"
  | "node_missing"
  | "needs_model"
  | "unknown";

export interface EngineReadiness {
  readonly ready: boolean;
  readonly reason: ReadinessReason;
  /** One sentence a person can act on. */
  readonly detail: string;
}

interface EnginesResponse {
  readonly engines: readonly string[];
  readonly current: string | null;
  readonly readiness?: Readonly<Record<string, EngineReadiness>>;
}

interface UseAgentEngineResult {
  readonly engines: readonly string[];
  readonly current: string | null;
  readonly loading: boolean;
  /** Per-engine verdict. An engine with no entry is NOT selectable. */
  readonly readiness: Readonly<Record<string, EngineReadiness>>;
  readonly setEngine: (name: string) => Promise<void>;
  /** Why the last `setEngine` was refused, or null. */
  readonly lastError: string | null;
}

const EMPTY: Readonly<Record<string, EngineReadiness>> = {};

export function useAgentEngine(): UseAgentEngineResult {
  const runtime = useAppRuntime();
  const [optimisticCurrent, setOptimisticCurrent] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useRpcQuery<EnginesResponse>(
    ["agent.engines"],
    "agent.engines",
    {},
    { retry: 3, retryDelay: 1000 },
  );

  const engines = data?.engines ?? [];
  const current = optimisticCurrent ?? data?.current ?? null;
  const readiness = data?.readiness ?? EMPTY;

  const setEngine = useCallback(
    async (name: string): Promise<void> => {
      setLastError(null);
      setOptimisticCurrent(name);
      try {
        await runtime.transport.rpc<{ engine: string }>("agent.set_engine", {
          engine: name,
        });
        void refetch();
      } catch (err: unknown) {
        // The backend refuses an engine that cannot run. Surface WHY —
        // silently reverting the selection is what made this look like a
        // dead control.
        setOptimisticCurrent(null);
        setLastError(err instanceof Error ? err.message : String(err));
      }
    },
    [runtime, refetch],
  );

  return { engines, current, loading: isLoading, readiness, setEngine, lastError };
}
